import { API_VALIDATE_ORGANIZATION_NAME } from '../constants/apiRoutes';
import { SYSTEM_ROLES } from '../constants/roles';
import { USER_STATUSES } from '../constants/statuses';
import { USER_BASIC_PERSONAL_INFORMATION } from '../constants/people';
import { PARAMS } from '../constants/pages';
import { ACTION_PLAN_STATUS_FILTERS_TYPES } from '../constants/filters';
import { systemRoleCheck } from './roles';
import { createCustomHttpClient } from './http';
import { showErrorMessage } from './uiUtils';

const NEUTRAL_ANSWER = { text: 'Neutral answer', weight: -1.0 };
const MAX_SCORE = 10;

export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const normalizeListOrder = (list, orderKey = 'order') =>
  list.map((listItem, index) => ({
    ...listItem,
    [orderKey]: index + 1,
  }));

const handleAnswers = (answers, attribute, isAiGenerated = false) => {
  const answerNum = attribute.with_zero_weight_answer
    ? attribute.answer_count - 1
    : attribute.answer_count;
  const weightIndex = answerNum ? MAX_SCORE / answerNum : answerNum;

  let answersList = answers.reduce((accum, current, index) => {
    const answerWeight = attribute.with_zero_weight_answer
      ? weightIndex * index
      : weightIndex * (index + 1);

    return [
      ...accum,
      {
        text: isAiGenerated ? current : current.text,
        order: index + 1,
        weight: Math.round(answerWeight * 10) / 10,
      },
    ];
  }, []);

  if (attribute.with_neutral_answer) {
    answersList = answersList.concat({
      ...NEUTRAL_ANSWER,
      order: answersList.length + 1,
    });
  }

  return answersList;
};

export const handleAnswerWeight = (
  questions,
  attribute,
  isAiGenerated = false
) => {
  return questions.reduce((acc, curr, questionIndex) => {
    let questionText = curr.text;
    let { answers } = curr;

    if (isAiGenerated) {
      const [key, value] = curr;
      questionText = attribute.text_reply_questions ? value : key;
      answers = value;
    }

    return [
      ...acc,
      {
        ...(curr.id ? { id: curr.id } : {}),
        unique_id: curr.unique_id
          ? curr.unique_id
          : Math.floor(Math.random() * 1000000),
        attribute: attribute.id,
        is_used_in_survey: curr.is_used_in_survey,
        is_archived: curr.is_archived,
        order_id: questionIndex + 1,
        text: questionText,
        with_neutral_answer: attribute.with_neutral_answer,
        answers: attribute.text_reply_questions
          ? []
          : [...handleAnswers(answers, attribute, isAiGenerated)],
      },
    ];
  }, []);
};

export const createSingleQuestion = (
  questionTitle,
  orderId,
  answerCount,
  attribute
) => {
  const answerNum = attribute.with_zero_weight_answer
    ? attribute.answer_count - 1
    : attribute.answer_count;
  const weightIndex = answerNum ? MAX_SCORE / answerNum : answerNum;
  let answers = [...Array(answerCount).keys()].map((_, i) => {
    const answerWeight = attribute.with_zero_weight_answer
      ? weightIndex * i
      : weightIndex * (i + 1);

    return {
      order: i + 1,
      weight: Math.round(answerWeight * 10) / 10,
      text: (i + 1).toString(),
    };
  });
  const neutralAnswer = {
    ...NEUTRAL_ANSWER,
    order: answerCount + 1,
  };
  answers = attribute.with_neutral_answer
    ? answers.concat(neutralAnswer)
    : answers;

  return {
    text: questionTitle,
    order_id: orderId,
    attribute: attribute?.id,
    is_archived: false,
    with_neutral_answer: attribute.with_neutral_answer,
    unique_id: Math.floor(Math.random() * 1000000),
    answer_count: answerCount,
    answers: answerCount !== 0 ? answers : [],
    is_used_in_survey: false,
  };
};

export const duplicateQuestion = (question, orderId, attribute) => {
  return {
    unique_id: Math.floor(Math.random() * 1000000),
    text: question.text,
    with_neutral_answer: attribute.with_neutral_answer,
    order_id: orderId,
    attribute: attribute.id,
    is_archived: false,
    answers: question.answers,
    is_used_in_survey: false,
  };
};

export const setIsUsedInSurveyField = (attr, questionUsageData) => {
  const updatedQuestionsList = attr.questions?.reduce((acc, curr) => {
    return [
      ...acc,
      {
        ...curr,
        is_used_in_survey: questionUsageData[curr.id],
      },
    ];
  }, []);

  return { ...attr, questions: updatedQuestionsList };
};

export const replaceObjectInList = (items, index, updatedProp) => {
  const updatedItem = { ...items[index], ...updatedProp };

  return [
    ...items.slice(0, index),
    updatedItem,
    ...items.slice(index + 1, items.length),
  ];
};

export const replaceObjectsInList = (list, listIndexes, replacementValues) => {
  let updatedList = [...list];
  listIndexes.map((listIndex, index) => {
    const updatedItem = { ...list[listIndex], ...replacementValues[index] };
    updatedList = [
      ...updatedList.slice(0, listIndex),
      updatedItem,
      ...updatedList.slice(listIndex + 1, list.length),
    ];
    return updatedList;
  });
  return updatedList;
};

export const isObject = value =>
  typeof value === 'object' && !Array.isArray(value) && value !== null;

export const isArray = value => Array.isArray(value);

export function isObjectEmpty(obj) {
  return Object.keys(obj).length === 0;
}

export function isArrayEmpty(array) {
  return isArray(array) && array.length === 0;
}

export const objectHasProperty = (object, property) => {
  return object[property] !== undefined;
};

export const trimString = string => string?.trim();

export const replacePropsInObject = (obj, props) => {
  return {
    ...obj,
    ...props,
  };
};

export const isOfType = (item, type) => {
  return typeof item === type;
};

export const isUserDeactivated = user => {
  const { DEACTIVATED } = USER_STATUSES;
  return user[USER_BASIC_PERSONAL_INFORMATION.STATUS] === DEACTIVATED;
};

export const isUserAppAccessForbidden = user => {
  const { ACTIVE_WITHOUT_ACCESS, DEACTIVATED } = USER_STATUSES;
  return user.status === ACTIVE_WITHOUT_ACCESS || user.status === DEACTIVATED;
};

export const getUnionOfTwoArrays = (firstArray, secondArray, key = 'id') => {
  const unique = new Set();

  return firstArray.concat(secondArray).filter(arrayItem => {
    return unique.has(arrayItem[key]) ? false : unique.add(arrayItem[key]);
  });
};

export const getArrayDifference = (arr1, arr2) => {
  const excludedIds = new Set(arr2.map(item => item.id));

  return arr1.filter(item => !excludedIds.has(item.id));
};

export const isArraySubset = (array, subsetArray) => {
  const set = new Set(array.map(item => item.id));
  if (set.size === 0) {
    return false;
  }
  return (
    subsetArray.filter(arrayItem => {
      return set.has(arrayItem.id);
    }).length === subsetArray.length
  );
};

export const asyncDebounce = (fn, delay = 150, callFirst = false) => {
  let timeout;
  let pendingArgs = null;
  let pendingThis = null;

  const debouncedFn = function (...args) {
    return new Promise(resolve => {
      const runFn = () => fn.apply(this, args);
      const callNow = callFirst && !timeout;

      if (!delay) {
        resolve(runFn());
        return;
      }

      pendingArgs = args;
      pendingThis = this;

      clearTimeout(timeout);

      timeout = setTimeout(() => {
        timeout = null;
        pendingArgs = null;
        pendingThis = null;
        if (!callNow) {
          resolve(runFn());
        }
      }, delay);

      if (callNow) {
        resolve(runFn());
      }
    });
  };

  debouncedFn.flush = function () {
    if (!timeout) {
      return Promise.resolve();
    }

    clearTimeout(timeout);
    timeout = null;

    const args = pendingArgs;
    const context = pendingThis;
    pendingArgs = null;
    pendingThis = null;

    return Promise.resolve(fn.apply(context, args));
  };

  return debouncedFn;
};

export const handleCustomHttpError = (error, defaultError) => {
  if (
    error &&
    error.response &&
    error.response.data &&
    error.response.data.detail
  ) {
    showErrorMessage(error.response.data.detail);
  } else {
    showErrorMessage(defaultError);
  }
};

export const areArraysContentsEqual = (arr1, arr2) => {
  return (
    isArray(arr1) &&
    isArray(arr2) &&
    arr1?.length === arr2?.length &&
    !arr1.some(val => !arr2.includes(val))
  );
};

export const getItemById = (items, id, idKey = 'id') =>
  items.find(item => item[idKey] === id);

export const getItemIndex = (items, id, idKey = 'id') =>
  items.findIndex(item => item[idKey] === id);

export const isItemInList = (list, item, key = 'id') =>
  list.some(i => i?.[key] === item?.[key]);

export const orderByPage = list => {
  const reorderedList = list.reduce((acc, curr) => {
    const exists = acc[curr.location];

    return {
      ...acc,
      [curr.location]: exists ? acc[curr.location].concat(curr) : [curr],
      active: {
        ...acc.active,
        ...(exists ? {} : { [curr.location]: curr.id }),
      },
    };
  }, {});

  return reorderedList;
};

export const isValueInList = (list, value) => list.some(item => item === value);

export const isSomeValueInList = (list1, list2) =>
  list2.some(value => isValueInList(list1, value));

export const isValidParameter = param => !Number.isNaN(Number(param)) && +param;

export const updatePersonRespondents = (respondents, id, value) => {
  const updatedRespondents = [...respondents];

  respondents.forEach(el => {
    if (!el.children) return;

    if (el.id === id) {
      el.children = value;
    } else {
      updatePersonRespondents(el.children, id, value);
    }
  });

  return updatedRespondents;
};

export const isEllipsisActive = (
  element,
  isVertical = false,
  isAlways = false
) => {
  if (!element) return false;
  const scrollHeight = element.offsetHeight < element.scrollHeight;
  const scrollWidth = element.offsetWidth < element.scrollWidth;

  if (isVertical) {
    return scrollHeight;
  }

  if (isAlways) {
    return scrollHeight || scrollWidth;
  }

  return scrollWidth;
};

export const prepareDonutChartData = (
  translations,
  data,
  isActionPlanFilter
) => {
  if (!data) return null;

  return Object.keys(data).reduce((acc, currentStatus) => {
    if (translations[currentStatus]) {
      return [
        ...acc,
        {
          name: translations[currentStatus],
          value: data[currentStatus],
          ...(isActionPlanFilter
            ? {
                filters: {
                  [PARAMS.COMPLETION_STATUS]: [
                    ACTION_PLAN_STATUS_FILTERS_TYPES[
                      currentStatus.toUpperCase()
                    ],
                  ],
                },
              }
            : { filters: { [PARAMS.COMPLETION_STATUS]: [currentStatus] } }),
        },
      ];
    }

    return acc;
  }, []);
};

export const getPersonShortName = person => {
  return person
    ? `${person.first_name} ${person.last_name.charAt(0).toUpperCase()}.`
    : '';
};

export const getEmployees = users =>
  users.filter(user => !systemRoleCheck(user, SYSTEM_ROLES.ASSOCIATE));

export const getAssociates = users =>
  users.filter(user => systemRoleCheck(user, SYSTEM_ROLES.ASSOCIATE));

export const getActiveUsers = users => {
  return users.filter(user => !isUserDeactivated(user));
};

export const getObjectToNumberArray = (array, key = 'id') =>
  array.reduce((acc, item) => [...acc, item[key]], []);

export const areArrayItemsReordered = (firstArray, secondArray) =>
  firstArray.some((item, index) => item.id !== secondArray[index]?.id);

export const removeObjectFromArray = (array, object, key = 'id') =>
  array.filter(item => item[key] !== object[key]);

export const removeArrayOfObjectsFromArray = (array1, array2, key = 'id') => {
  return array1.filter(item => !isItemInList(array2, item, key));
};

export const mapObjectsArrayFromNumbersArray = (
  arrayOfObjects,
  arrayOfNumbers,
  key = 'id'
) => arrayOfObjects.filter(obj => isValueInList(arrayOfNumbers, obj[key]));

export const areValuesChanged = (arr1, arr2, fields = []) => {
  return arr1.some(el => {
    const element = getItemById(arr2, el.id);
    return Boolean(fields.find(field => el[field] !== element[field]));
  });
};

export const getFormattedUsers = (users, allUserInformation = false) => {
  const formattedUsers = {};
  users.forEach(user => {
    const userInfo = allUserInformation ? user : user.id;
    if (formattedUsers[user.report_to]) {
      formattedUsers[user.report_to] =
        formattedUsers[user.report_to].concat(userInfo);
    } else {
      formattedUsers[user.report_to] = [userInfo];
    }
  });

  return formattedUsers;
};

export const getAllManagersUnder = (userId, formattedModeratorsWithTeams) => {
  let list = [userId];
  let index = 0;
  while (index < list.length) {
    const users = formattedModeratorsWithTeams[list[index]];
    list = users ? list.concat(users) : list;
    index += 1;
  }
  return list;
};

export const reorderItemsInArrayByIds = (array, arrayIds) =>
  arrayIds.reduce((acc, currentId) => {
    const reorderedItem = array.find(item => item.id === currentId);

    return [...acc, reorderedItem];
  }, []);

export const reorderAndUpdateItemsInArrayByIds = (array, arrayIds) =>
  arrayIds.reduce((acc, currentId, currentIndex) => {
    const reorderedItem = array.find(item => item.id === currentId);
    reorderedItem.order = currentIndex + 1;

    return [...acc, reorderedItem];
  }, []);

export const calculatePercentageToNDecimals = (part, total, n = 1) => {
  if (part && total) {
    const powerOf10 = 10 ** (n > 0 ? n : 0);
    const rawValue = (part * 100) / total;

    return Math.round((rawValue + Number.EPSILON) * powerOf10) / powerOf10;
  }

  return 0;
};

export const calcLargestRemainderPercentage = responses => {
  const TIE_BREAKER_SCALE = 10000000000;
  const SCALE_FACTOR = 10000;
  const DISTRIBUTION_POINTS = 100;
  const desiredTotalInt = 10000;

  const items = [];
  let actualScaledSum = 0;

  const remainderBuckets = Array.from(
    { length: DISTRIBUTION_POINTS },
    () => []
  );

  for (let index = 0; index < responses.length; index += 1) {
    const response = responses[index];
    const scaledMain = Math.round(response * SCALE_FACTOR);
    const mainRemainder = scaledMain % DISTRIBUTION_POINTS;
    const scaledTieBreaker = response * TIE_BREAKER_SCALE;
    const tieBreakerRemainder = scaledTieBreaker % TIE_BREAKER_SCALE;

    const item = {
      originalIndex: index,
      finalScaledValue: scaledMain,
      mainRemainder,
    };
    items.push(item);
    actualScaledSum += scaledMain;

    remainderBuckets[mainRemainder].push({
      itemIndex: index,
      tieBreaker: tieBreakerRemainder,
    });
  }

  const difference = desiredTotalInt - actualScaledSum;

  if (difference !== 0) {
    let adjustmentsMade = 0;
    const direction = difference > 0 ? 1 : -1;
    const adjustmentsNeeded = Math.abs(difference);

    for (
      let r = DISTRIBUTION_POINTS - 1;
      r >= 0 && adjustmentsMade < adjustmentsNeeded;
      r -= 1
    ) {
      const bucket = remainderBuckets[r];

      if (bucket.length > 0) {
        bucket.sort((a, b) => b.tieBreaker - a.tieBreaker);

        for (
          let i = 0;
          i < bucket.length && adjustmentsMade < adjustmentsNeeded;
          i += 1
        ) {
          const { itemIndex } = bucket[i];
          items[itemIndex].finalScaledValue += direction;
          adjustmentsMade += 1;
        }
      }
    }
  }

  const finalPercentages = new Array(items.length);

  items.forEach(item => {
    const percentage = parseFloat(
      (item.finalScaledValue / DISTRIBUTION_POINTS).toFixed(2)
    );
    finalPercentages[item.originalIndex] = percentage;
  });

  return finalPercentages;
};

export const prepareSubmitedSurveyParticipantsCount = (
  surveyParticipants = []
) =>
  surveyParticipants.reduce((acc, participant) => {
    if (participant.finished) {
      return acc + 1;
    }
    return acc;
  }, 0);

export const validateOrganizationName = orgName => {
  return createCustomHttpClient().get(API_VALIDATE_ORGANIZATION_NAME, {
    params: { q: orgName },
  });
};

export const prepareFilterWithAllOption = (allLabel, items) => [
  { id: null, name: allLabel },
  ...items,
];
